'use client';

import { useForm } from '@tanstack/react-form';
import { ArrowRight, Boxes, Globe2, Network } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import FormInput from '../../../components/form-input';
import { signIn } from '../../../lib/auth/client';
import { Button } from '../../../ui/button';

export default function WholesaleSigninForm() {
  const router = useRouter();

  const form = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
    onSubmit: async ({ value }) => {
      try {
        const { error } = await signIn.email({
          email: value.email,
          password: value.password,
        });

        if (error) {
          toast.error(error.message || 'Invalid email or password');
        } else {
          toast.success('Welcome back!');
          router.push('/stores/join-os');
        }
      } catch {
        toast.error('Something went wrong');
      }
    },
  });

  return (
    <div className="grid overflow-hidden rounded-[1.75rem] border border-border/70 bg-card shadow-[0_30px_90px_rgba(15,23,42,0.12)] lg:grid-cols-[1.05fr_0.95fr]">
      <SignInNetworkPanel />

      <div className="flex min-h-[590px] items-center px-6 py-10 sm:px-10 lg:px-14">
        <form
          className="mx-auto w-full max-w-md"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className="mb-8">
            <span className="inline-flex rounded-full border border-brand/20 bg-brand/[0.06] px-3 py-1 text-xs font-medium text-brand">
              OS Wholesale
            </span>
            <h1 className="mt-5 text-3xl font-semibold tracking-[-0.035em] text-foreground sm:text-4xl">
              Welcome back
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Sign in to manage inventory, orders, and wholesale operations.
            </p>
          </div>

          <div className="space-y-5">
            <form.Field name="email">
              {(field) => (
                <FormInput
                  field={field}
                  id="wholesale-signin-email"
                  data-testid="wholesale-signin-email"
                  type="email"
                  label="Email address"
                  inputClassName="h-12 rounded-xl bg-background px-4"
                  autoComplete="email"
                  placeholder="name@company.com"
                />
              )}
            </form.Field>

            <form.Field name="password">
              {(field) => (
                <FormInput
                  field={field}
                  id="wholesale-signin-password"
                  data-testid="wholesale-signin-password"
                  type="password"
                  label="Password"
                  inputClassName="h-12 rounded-xl bg-background px-4"
                  autoComplete="current-password"
                  placeholder="••••••••"
                />
              )}
            </form.Field>
          </div>

          <div className="mt-3 text-right">
            <Link
              href="/auth/forgot-password"
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Forgot password?
            </Link>
          </div>

          <div className="mt-7">
            <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
              {([canSubmit, isSubmitting]) => (
                <Button
                  type="submit"
                  data-testid="wholesale-signin-submit"
                  disabled={!canSubmit}
                  className="h-12 w-full rounded-xl bg-foreground px-5 text-sm font-medium text-background shadow-[0_12px_30px_rgba(15,23,42,0.14)] transition-colors hover:bg-foreground/85 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <svg
                        className="-ml-1 mr-2 h-4 w-4 animate-spin motion-reduce:animate-none"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <title>Signing in</title>
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Signing in...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Sign in
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </span>
                  )}
                </Button>
              )}
            </form.Subscribe>
          </div>

          <div className="my-7 flex items-center">
            <div className="flex-grow border-t border-border" />
            <span className="mx-4 flex-shrink text-xs uppercase tracking-[0.16em] text-muted-foreground">
              New to OS?
            </span>
            <div className="flex-grow border-t border-border" />
          </div>

          <Link
            href="/stores/join-os/application"
            className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-border bg-background px-5 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            Apply for a wholesale account
          </Link>
        </form>
      </div>
    </div>
  );
}

const networkCapabilities = [
  { icon: Boxes, label: 'Inventory' },
  { icon: Network, label: 'Distribution' },
  { icon: Globe2, label: 'Global demand' },
];

function SignInNetworkPanel() {
  return (
    <aside className="relative hidden min-h-[590px] overflow-hidden bg-[#050b1a] p-10 text-white lg:flex lg:flex-col lg:justify-between xl:p-14">
      <div
        className="pointer-events-none absolute -bottom-10 -right-32 h-[420px] w-[660px] text-brand opacity-[0.24]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 2px 2px, currentColor 1.15px, transparent 1.35px)',
          backgroundSize: '7px 7px',
          WebkitMaskImage: "url('/images/world-map-robinson.svg')",
          WebkitMaskPosition: 'center',
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskSize: 'contain',
          maskImage: "url('/images/world-map-robinson.svg')",
          maskPosition: 'center',
          maskRepeat: 'no-repeat',
          maskSize: 'contain',
        }}
      />

      <svg
        className="pointer-events-none absolute bottom-2 right-0 h-80 w-[560px] text-brand"
        viewBox="0 0 560 320"
        aria-hidden="true"
      >
        <g fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.4">
          <path d="M54 190 C155 115 245 126 300 172" />
          <path d="M54 190 C146 216 233 208 300 172" />
          <path d="M300 172 C370 124 450 143 516 225" />
          <path d="M300 172 C388 179 459 195 516 225" strokeDasharray="5 7" />
        </g>
        <g fill="currentColor">
          <circle cx="54" cy="190" r="5" />
          <circle cx="300" cy="172" r="5" />
          <circle cx="516" cy="225" r="5" />
        </g>
      </svg>

      <div className="relative z-10 max-w-lg">
        <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-3 py-1.5 text-xs font-medium text-white/75">
          <span className="h-1.5 w-1.5 rounded-full bg-brand" />
          Commerce infrastructure
        </span>
        <h2 className="mt-7 text-4xl font-semibold leading-[1.05] tracking-[-0.045em] text-white xl:text-5xl">
          Your wholesale network, connected.
        </h2>
        <p className="mt-5 max-w-md text-base leading-relaxed text-white/60">
          One workspace to source, sell, distribute, and optimize inventory across every channel.
        </p>
      </div>

      <ul className="relative z-10 grid grid-cols-3 gap-3">
        {networkCapabilities.map((capability) => {
          const Icon = capability.icon;
          return (
            <li
              key={capability.label}
              className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 backdrop-blur-sm"
            >
              <Icon className="h-5 w-5 text-brand" strokeWidth={1.8} aria-hidden="true" />
              <span className="mt-3 block text-xs font-medium text-white/75">
                {capability.label}
              </span>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
